import React, { useCallback, useEffect, useRef, useState } from "react";
import yaml from "yaml";
import {
  Button,
  Alert,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@heroui/react";
import { Switch } from "@heroui/switch";
import { Icon } from "@iconify/react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@webuild-ai/react-auth";
import { useBrand } from "@webuild-ai/react-auth";

import { useWorkflowStore, workflowToYamlObject } from "@/stores/WorkflowStore";
import WorkflowCodeEditor from "@/components/WorkflowCodeEditor/WorkflowCodeEditor";
import WorkflowViewer from "@/components/WorkflowViewer/WorkflowViewer";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { PromptWithActions } from "@/components/Chat/PromptWithActions";
import { ChatPanel } from "@/components/Chat/ChatPanel";
import { useChatStore } from "@/stores/ChatStore";
import { useChatHistoryStore } from "@/stores/ChatHistoryStore";
import { PromptWithActionsInvestAI } from "@/components/Chat/PromptWithActions-InvestAI";
import { PageTitle } from "@/components/UI/PageTitle";
import { convertWorkflowOutputToConfig } from "@/utils/workflowUtils";
import { formatErrorForDisplay } from "@/utils/errorFormatter";
import { subscribeAndInvoke } from "@/utils/chat";
import { logger } from "@/telemetry";

export const WorkflowEditPage: React.FC = () => {
  const { workflowId } = useParams();
  const { workflow, loadWorkflow, setWorkflowFromYaml, saveWorkflow, setWorkflow } =
    useWorkflowStore();
  const { adminClient, platformClient, sseWorkflowClient } = useWebuildAPIClient();
  const { create, invoke, chat, runId, chatWorkflowId, newChat, subscribe, streaming } =
    useChatStore();
  const { fetchChatHistory } = useChatHistoryStore();
  const { t } = useTranslation();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [errorMsg, setErrorMsg] = useState("");
  const [isShowPrompt, setIsShowPrompt] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editorValue, setEditorValue] = useState("");
  const [leftWidth, setLeftWidth] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [showCreated, setShowCreated] = useState(false);
  const [createdWorkflowName, setCreatedWorkflowName] = useState("");
  const [hasManualEdit, setHasManualEdit] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const { brand } = useBrand();

  // Check if clients are ready
  const clientsReady = !authLoading && isAuthenticated && adminClient && platformClient;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Clear WorkflowStore so stale data doesn't leak to other pages
      setWorkflow(null);
    };
  }, [setWorkflow]);

  useEffect(() => {
    if (chatWorkflowId && chatWorkflowId !== "workflow_generator") {
      newChat();
    }
  }, [chatWorkflowId, newChat]);

  // Track the last cleared workflowId to prevent clearing during generation
  const lastClearedWorkflowIdRef = useRef<string | undefined>(undefined);

  // Clear workflow when workflowId changes to prevent showing stale data
  useEffect(() => {
    if (!workflowId || !workflow) return;

    // Only clear once per workflowId change, not on every workflow update
    if (lastClearedWorkflowIdRef.current === workflowId) return;

    // Clear if navigating TO "new" from an existing workflow that was already saved
    // Don't clear if we're generating (workflow is being built from YAML)
    if (workflowId === "new" && workflow.id && !streaming && !runId) {
      setWorkflow(null);
      setEditorValue("");
      lastClearedWorkflowIdRef.current = workflowId;

      return;
    }

    // Clear if switching between different existing workflows
    if (workflow.id && workflowId !== "new" && workflow.id !== workflowId) {
      setWorkflow(null);
      setEditorValue("");
      lastClearedWorkflowIdRef.current = workflowId;
    }
  }, [workflowId, workflow, setWorkflow, streaming, runId]);

  // Track the previous workflowId to detect navigation changes
  const prevWorkflowIdRef = useRef<string | undefined>(undefined);

  // Clear chat and manage view when navigating between workflows
  useEffect(() => {
    const isEditingExisting = workflowId && workflowId !== "new";
    const isCreatingNew = workflowId === "new";
    const workflowIdChanged = prevWorkflowIdRef.current !== workflowId;

    if (isEditingExisting) {
      // Clear any workflow_generator chat for existing workflows
      if (runId || chatWorkflowId === "workflow_generator") {
        newChat();
      }
      // Switch to code view for existing workflows
      setIsShowPrompt(false);
    } else if (isCreatingNew && workflowIdChanged) {
      // User navigated TO the new workflow page - clear everything to start fresh
      // Only if coming from a different page (not initial load)
      if (prevWorkflowIdRef.current !== undefined) {
        newChat();
        setWorkflow(null);
        setEditorValue("");
        setHasManualEdit(false);
      }
      // Ensure we're on the prompt view for new workflows
      setIsShowPrompt(true);
    }

    // Update the previous workflowId
    prevWorkflowIdRef.current = workflowId;
  }, [workflowId, chatWorkflowId, newChat, runId, setWorkflow]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;

      setLeftWidth(Math.min(Math.max(newWidth, 5), 95)); // Limit between 5% and 95%
    },
    [isDragging],
  );

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMouseMove]);

  useEffect(() => {
    const loadWorkflowData = async () => {
      // Skip loading for new workflows - they don't exist yet
      if (!workflowId || workflowId === "new" || !clientsReady) {
        return;
      }

      try {
        await loadWorkflow(workflowId, adminClient);
      } catch (error) {
        logger.error("Failed to load workflow", error);
      }
    };

    loadWorkflowData();
  }, [workflowId, clientsReady, adminClient, loadWorkflow]);

  const isNewWorkflow = workflowId === "new" || !workflow?.id;

  const handleSave = async () => {
    if (!clientsReady) {
      setErrorMsg(
        t("workflowEditor.clientsNotReady", {
          defaultValue: "Still loading — please wait a moment and try again.",
        }),
      );

      return;
    }

    setIsSaving(true);
    try {
      const wasNewWorkflow = isNewWorkflow;
      const workflowName = workflow?.name || "";

      await saveWorkflow(adminClient);

      if (wasNewWorkflow) {
        // Show success modal for new workflow creation
        setCreatedWorkflowName(workflowName);
        setShowCreated(true);

        // Navigate to the workflow page after a short delay
        timeoutRef.current = setTimeout(() => {
          if (workflow?.id) {
            navigate(`/workflows/${workflow.id}`);
          }
        }, 2000);
      }
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 5000);
    } catch (error: any) {
      setErrorMsg(
        t("workflowEditor.saveError", {
          defaultValue: "Something went wrong while saving the workflow: {{detail}}",
          detail: formatErrorForDisplay(error),
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const openChat = async () => {
    navigate(`/workflows/${workflow?.id}`);
  };

  const submit = async (prompt: string) => {
    if (!clientsReady) {
      setErrorMsg(
        t("workflowEditor.clientsNotReady", {
          defaultValue: "Still loading — please wait a moment and try again.",
        }),
      );

      return;
    }
    const userId = user?.id;

    if (!userId) {
      setErrorMsg("User not found.");

      return;
    }

    // Reset manual edit flag when submitting new prompt
    setHasManualEdit(false);

    try {
      if (!runId) {
        const promptGeneratorWorkflow = await adminClient.workflows.getWorkflow({
          workflowId: "workflow_generator",
        });

        if (!user) {
          throw new Error("User not found");
        }

        // Convert admin WorkflowOutput to agent-platform WorkflowConfig
        const workflowConfig = convertWorkflowOutputToConfig(promptGeneratorWorkflow);

        await create(platformClient, workflowConfig, user.id, user.tenant, "summary");
      }
      await subscribeAndInvoke({
        sseWorkflowClient,
        adminClient,
        platformClient,
        userId,
        fetchChatHistory,
        message: { prompt },
        subscribe,
        invoke,
      });
    } catch {
      setErrorMsg(
        t("workflowEditor.submitError", {
          defaultValue: "Something went wrong while sending your message. Please try again.",
        }),
      );
    }
  };

  const updateWorkflowFromCode = async (code: string) => {
    if (!clientsReady) {
      setErrorMsg(
        t("workflowEditor.clientsNotReady", {
          defaultValue: "Still loading — please wait a moment and try again.",
        }),
      );

      return;
    }

    // Mark that user has manually edited the code
    setHasManualEdit(true);

    try {
      await setWorkflowFromYaml(code, platformClient);
      setErrorMsg("");
    } catch (error: any) {
      let detail: string;

      try {
        detail = error.body
          ? formatErrorForDisplay(JSON.parse(error.body))
          : formatErrorForDisplay(error);
      } catch {
        detail = formatErrorForDisplay(error);
      }

      setErrorMsg(
        t("workflowEditor.configCodeError", {
          defaultValue: "There's an issue with the configuration code: {{detail}}",
          detail,
        }),
      );
    }
  };

  useEffect(() => {
    if (!clientsReady) {
      return;
    }

    // Only auto-update from messages if user hasn't manually edited
    // OR if we're currently streaming (to show live updates)
    if (hasManualEdit && !streaming) {
      return;
    }

    const processLastMessage = async () => {
      if (!chat?.messages?.length) return;

      // Find the last assistant message (most recent)
      const lastAssistantMessage = chat.messages
        .slice()
        .reverse()
        .find((message) => message.role === "assistant");

      if (!lastAssistantMessage) return;

      let messageContent;

      if (typeof lastAssistantMessage.content === "object") {
        messageContent =
          typeof lastAssistantMessage.content.prompt === "string"
            ? lastAssistantMessage.content.prompt
            : "";
      } else {
        messageContent = lastAssistantMessage.content;
      }

      const yamlMatch = messageContent.match(/```yaml\n([\s\S]*?)```/);

      if (!yamlMatch) return;

      const yamlContent = yamlMatch[1];

      try {
        setEditorValue(yamlContent);
        await setWorkflowFromYaml(yamlContent, platformClient);
        setErrorMsg("");

        return;
      } catch (error: any) {
        let detail: string;

        try {
          detail = error.body
            ? formatErrorForDisplay(JSON.parse(error.body))
            : formatErrorForDisplay(error);
        } catch {
          detail = formatErrorForDisplay(error);
        }

        setErrorMsg(
          t("workflowEditor.generatedConfigError", {
            defaultValue:
              "The generated configuration has an issue. Please try asking the assistant to fix it: {{detail}}",
            detail,
          }),
        );
      }
    };

    processLastMessage();
  }, [
    chat?.messages,
    streaming,
    clientsReady,
    platformClient,
    setWorkflowFromYaml,
    hasManualEdit,
    t,
  ]);

  // Show loading state while clients are initializing
  if (authLoading || !isAuthenticated) {
    return (
      <div className="flex flex-col h-screen" data-id="workflow-edit-container">
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-lg">
              {authLoading ? t("Loading...") : t("Please log in to continue")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!clientsReady) {
    return (
      <div className="flex flex-col h-screen" data-id="workflow-edit-container">
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-lg">{t("Initialising clients...")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen" data-id="workflow-edit-container">
      {/* Success Modal for Workflow Creation */}
      <Modal
        backdrop="blur"
        classNames={{
          backdrop: "bg-gradient-to-t from-zinc-900 to-zinc-900/10 backdrop-opacity-20",
        }}
        isOpen={showCreated}
        placement="center"
        onClose={() => setShowCreated(false)}
      >
        <ModalContent>
          <ModalHeader className="flex items-center gap-3">
            <Icon className="text-brand-primary" icon="solar:check-circle-bold" width={24} />
            <span>{t("Workflow Created Successfully")}</span>
          </ModalHeader>
          <ModalBody>
            <p className="text-default-600 whitespace-pre-line leading-relaxed">
              {t("The workflow")} <strong>{createdWorkflowName}</strong>{" "}
              {t("was created successfully")}.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              className="bg-brand-primary text-white"
              onPress={() => {
                setShowCreated(false);
                if (workflow?.id) {
                  navigate(`/workflows/${workflow.id}`);
                }
              }}
            >
              {t("Open Workflow")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <div className="flex items-center justify-center py-4 px-6" data-id="workflow-edit-header">
        <PageTitle
          centered
          dataId="agent-studio-title"
          icon="solar:widget-2-outline"
          img={`/brands/${brand.prefix || "webuild"}/agent.svg`}
          title="Agent Studio"
        />
      </div>
      <div className="flex flex-col justify-center items-center" data-id="workflow-edit-alerts">
        {errorMsg && <Alert color="danger" data-id="workflow-error-alert" title={errorMsg} />}
        {showSaved && (
          <Alert color="success" data-id="workflow-saved-alert" title={t("Workflow saved")} />
        )}
      </div>

      <div
        ref={containerRef}
        className="flex w-full h-[calc(100vh-8rem)] relative"
        data-id="workflow-edit-content"
      >
        {leftWidth > 0 && (
          <div
            className="h-full"
            data-id="workflow-edit-left-panel"
            style={{ width: `${leftWidth}%` }}
          >
            <div className="flex items-center px-5 h-16" data-id="workflow-edit-left-header">
              <Switch
                classNames={{
                  wrapper: "bg-brand-primary",
                }}
                data-id="workflow-view-switch"
                endContent={<Icon className="text-lg text-primary" icon="gravity-ui:code" />}
                isDisabled={!isNewWorkflow}
                isSelected={isShowPrompt}
                size="lg"
                startContent={
                  <Icon className="text-lg text-primary" icon="gravity-ui:face-robot" />
                }
                onValueChange={setIsShowPrompt}
              />
              <div className="flex-1" />
              <Button
                isIconOnly
                aria-label="Settings"
                className="hover:bg-gray-100"
                data-id="left-panel-expand-button"
                size="lg"
                variant="light"
                onPress={() => setLeftWidth(leftWidth === 100 ? 50 : 100)}
              >
                <Icon
                  className="text-xl"
                  icon={
                    leftWidth === 100
                      ? "gravity-ui:chevrons-collapse-up-right"
                      : "gravity-ui:chevrons-expand-up-right"
                  }
                />
              </Button>
            </div>

            {isShowPrompt ? (
              <div className="h-[calc(100%-4rem)]" data-id="workflow-prompt-container">
                <div className="relative flex h-full flex-col" data-id="workflow-chat-container">
                  {chat && runId && chat.messages.length > 0 && (
                    <div className="flex-1 overflow-hidden">
                      <ChatPanel
                        data-id="workflow-chat-panel"
                        messages={chat.messages}
                        runId={runId}
                      />
                    </div>
                  )}
                  <div className="flex-shrink-0">
                    {brand.name === "Invest Ai" ? (
                      <PromptWithActionsInvestAI
                        data-id="workflow-prompt-actions"
                        features={[]}
                        showFileUpload={false}
                        showKnowledgeHub={false}
                        showPrompts={false}
                        showSearch={false}
                        showVoice={false}
                        streaming={streaming}
                        submit={submit}
                        suggestions={[]}
                      />
                    ) : (
                      <PromptWithActions
                        data-id="workflow-prompt-actions"
                        features={[]}
                        showFileUpload={true}
                        showKnowledgeHub={false}
                        showPrompts={false}
                        showSearch={false}
                        showVoice={true}
                        streaming={streaming}
                        submit={submit}
                        suggestions={[]}
                      />
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-[calc(100%-4rem)]" data-id="workflow-code-container">
                <WorkflowCodeEditor
                  code={workflow ? yaml.stringify(workflowToYamlObject(workflow)) : editorValue}
                  data-id="workflow-code-editor"
                  onChange={updateWorkflowFromCode}
                />
              </div>
            )}
          </div>
        )}
        <div
          className="w-1 bg-gray-300 hover:bg-gray-400 cursor-col-resize"
          data-id="workflow-resize-handle"
          style={{ cursor: isDragging ? "col-resize" : "default" }}
          onMouseDown={handleMouseDown}
        />
        {leftWidth !== 100 && (
          <div
            className="h-full"
            data-id="workflow-edit-right-panel"
            style={{ width: `${100 - leftWidth}%` }}
          >
            <div
              className="flex justify-between items-center h-16 px-4"
              data-id="workflow-edit-right-header"
            >
              <Button
                isIconOnly
                aria-label="Settings"
                className="hover:bg-gray-100"
                data-id="right-panel-collapse-button"
                size="lg"
                variant="light"
                onPress={() => setLeftWidth(leftWidth === 0 ? 50 : 0)}
              >
                <Icon
                  className="text-xl"
                  icon={
                    leftWidth === 0
                      ? "gravity-ui:chevrons-collapse-up-right"
                      : "gravity-ui:chevrons-expand-up-right"
                  }
                />
              </Button>
              <div className="flex gap-2" data-id="workflow-action-buttons">
                <Button
                  className="bg-brand-primary text-white"
                  data-id="workflow-save-button"
                  isDisabled={isSaving || !clientsReady}
                  onPress={handleSave}
                >
                  {isNewWorkflow ? t("Create") : t("Save")}
                </Button>
                {!isNewWorkflow && (
                  <Button
                    className="bg-brand-primary text-white"
                    data-id="workflow-open-chat-button"
                    isDisabled={isSaving || !clientsReady}
                    onPress={openChat}
                  >
                    {t("Open in Chat")}
                  </Button>
                )}
              </div>
            </div>

            <div className="h-[calc(100%-4rem)]" data-id="workflow-viewer-container">
              {workflow && <WorkflowViewer data-id="workflow-viewer" workflow={workflow} />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
